import React, { useState } from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Alert, Box, Button, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';

import type { LensDatasetField } from '~/types/leLens';

import { prepareFields } from './datasetFields';

/**
 * Calculated fields on a dataset: a name, a formula, and whether it is worked
 * out per row or per group.
 *
 * "When" is presented as the choice it is rather than as a technical setting,
 * because it is the one that decides whether a rate is right. The faults below
 * come from the same check the renderer runs, so a formula that will not
 * compute says so here rather than rendering an empty card later.
 */
export default function DatasetFieldsEditor({
  columns,
  disabled,
  fields,
  onChange,
}: {
  columns: Array<string>;
  disabled?: boolean;
  fields: Array<LensDatasetField>;
  onChange: (fields: Array<LensDatasetField>) => void;
}) {
  const [showHelp, setShowHelp] = useState(false);
  const prepared = prepareFields(fields, columns);
  const faultFor = (name: string) => prepared.faults.find((fault) => fault.name === name)?.reason;

  const update = (index: number, patch: Partial<LensDatasetField>) =>
    onChange(fields.map((field, position) => (position === index ? { ...field, ...patch } : field)));

  return (
    <Box sx={{ mt: 2 }}>
      <Stack alignItems="center" direction="row" spacing={1} sx={{ mb: 0.5 }}>
        <Typography color="text.secondary" variant="caption">
          Calculated fields
        </Typography>
        <Button onClick={() => setShowHelp((open) => !open)} size="small" sx={{ minWidth: 0, p: 0 }}>
          {showHelp ? 'Hide formulas' : 'What can I write?'}
        </Button>
      </Stack>

      {showHelp && (
        <Alert severity="info" sx={{ mb: 1 }} variant="outlined">
          <Typography sx={{ display: 'block' }} variant="caption">
            Arithmetic (<code>+ - * /</code>) and parentheses over this dataset&rsquo;s columns, comparisons (
            <code>= &lt;&gt; &gt; &gt;= &lt; &lt;=</code>), <code>and</code> / <code>or</code> / <code>not</code>,
            quoted text, and <code>if(test, then, otherwise)</code>, <code>coalesce(a, b)</code>, <code>abs(x)</code>,{' '}
            <code>round(x, places)</code>. Dividing by zero gives no value rather than an error.
          </Typography>
          <Typography color="text.secondary" sx={{ display: 'block', mt: 0.5 }} variant="caption">
            Columns available: {columns.join(', ') || 'run the query to see its columns'}
          </Typography>
        </Alert>
      )}

      <Stack spacing={1}>
        {fields.map((field, index) => {
          const fault = faultFor(field.name?.trim() ?? '');
          return (
            <Stack alignItems="flex-start" direction="row" key={index} spacing={1}>
              <TextField
                disabled={disabled}
                error={Boolean(fault)}
                label="Name"
                onChange={(event) => update(index, { name: event.target.value })}
                size="small"
                sx={{ width: 180 }}
                value={field.name ?? ''}
              />
              <TextField
                disabled={disabled}
                error={Boolean(fault)}
                fullWidth
                helperText={fault}
                label="Formula"
                onChange={(event) => update(index, { expression: event.target.value })}
                size="small"
                sx={{ '& input': { fontFamily: 'monospace', fontSize: '12.5px' } }}
                value={field.expression ?? ''}
              />
              <TextField
                disabled={disabled}
                helperText={field.when === 'row' ? 'Before grouping' : 'A column means its total'}
                label="Worked out"
                onChange={(event) => update(index, { when: event.target.value as LensDatasetField['when'] })}
                select
                size="small"
                sx={{ width: 190 }}
                value={field.when ?? 'aggregate'}
              >
                <MenuItem value="aggregate">On totals</MenuItem>
                <MenuItem value="row">On each row</MenuItem>
              </TextField>
              <Tooltip title={`Remove ${field.name || 'this field'}`}>
                <IconButton
                  aria-label={`Remove ${field.name || 'this field'}`}
                  disabled={disabled}
                  onClick={() => onChange(fields.filter((_entry, position) => position !== index))}
                  size="small"
                  sx={{ mt: 0.5 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        })}
      </Stack>

      <Button
        disabled={disabled}
        onClick={() => onChange([...fields, { name: '', expression: '', when: 'aggregate' }])}
        size="small"
        sx={{ mt: fields.length ? 1 : 0 }}
      >
        Add a field
      </Button>

      {fields.length === 0 && (
        <Typography color="text.secondary" sx={{ display: 'block' }} variant="caption">
          A rate, share or average belongs here rather than in the query: worked out on totals, it stays correct however
          a card groups the rows.
        </Typography>
      )}
    </Box>
  );
}
